'use client'

import { useMemo, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'
import Image from 'next/image'
import { ArrowRight, BanknoteArrowUp, ChevronDown, ChevronLeft, ChevronRight, Crown, Download, FileDown, FileText, Hammer, Handshake, Heart, LoaderCircle, Medal, Minus, Percent, Plus, ShoppingBag, Truck } from 'lucide-react'
import { useCart, useWishlist, useVariantSelection, useProductGallery, useProductQuantity, useCartActions } from '@/lib/commerce/hooks'
import { formatImageURL, formatPrice, getEffectivePrice, getStrikethroughPrice, getDisplayDiscounts, isDiscountBetter, getSalePercent, isCustomerAuthActive, getYoutubeVideos, getYoutubeVideoId, getVariantDimensions, getProductTags, formatFileSize, getBuyXGetYTagText, getBuyXGetYFallbackTitle, getLoyaltyProductDiscount } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import { Button } from '@/components/elements/Button'
import ProductDescription from '@/components/blocks/ProductDescription'
import TermsDrawer from '@/components/blocks/TermsDrawer'
import StorepayDrawer from '@/components/blocks/StorepayDrawer'
import PocketDrawer from '@/components/blocks/PocketDrawer'
import LendDrawer from '@/components/blocks/LendDrawer'
import Qpay from '@/assets/qpay.svg'
import TransferIcon from '@/assets/transfer.png'
import Storepay from '@/assets/storepay.png'
import Pocket from '@/assets/pocket.png'
import Lend from '@/assets/lendmn.png'
import Omniway from '@/assets/omniway.png'
import ProductCard from './ProductCard'
import Review from './Review'
import VariantOptionPicker from '@/components/blocks/VariantOptionPicker'
import { hasOptions } from '@/lib/commerce/variantOptions'

type TermsType = 'delivery' | 'payment' | 'service'

type Tab = 'description' | 'reviews' | 'features' | 'payment' | 'digital' | 'order' | 'dimensions'

function Stars({ value, size = 16 }: { value: number, size?: number }) {
  return (
    <div className="inline-flex items-center gap-0.5 text-foreground">
      {[1, 2, 3, 4, 5].map(i => {
        const filled = i <= Math.round(value)
        return (
          <svg key={i} width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
            <path d="M12 2 9 9H2l5.5 4L5 22l7-5 7 5-2.5-9L22 9h-7z" />
          </svg>
        )
      })}
    </div>
  )
}

function RelatedRail({ title, products, vendor }: { title: string, products: any[], vendor: any }) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  if (!products || products.length === 0) return null
  const showArrows = products.length > 5
  const scroll = (dir: number) => {
    const el = scrollerRef.current
    if (!el) return
    el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: 'smooth' })
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[22px] sm:text-[26px] font-semibold tracking-[-0.0075em] font-gilroy">{title}</h2>
        {showArrows && (
          <div className="flex gap-2">
            <button onClick={() => scroll(-1)} className="w-10 h-10 rounded-full bg-[#F1EEE7] hover:bg-[#E9E4DA] text-foreground inline-flex items-center justify-center cursor-pointer transition-colors"><ChevronLeft className="size-4" /></button>
            <button onClick={() => scroll(1)} className="w-10 h-10 rounded-full bg-[#F1EEE7] hover:bg-[#E9E4DA] text-foreground inline-flex items-center justify-center cursor-pointer transition-colors"><ChevronRight className="size-4" /></button>
          </div>
        )}
      </div>
      <div ref={scrollerRef} className="flex gap-4 overflow-x-auto pb-2 snap-x scroll-px-1 -mx-1 px-1">
        {products.map(p => (
          <div key={p.customId || p.id} className="w-[220px] shrink-0 snap-start">
            <ProductCard product={p} vendor={vendor} />
          </div>
        ))}
      </div>
    </div>
  )
}

export default function ProductDetail({ productsList, product, vendor, reviews }: { productsList: any[], product: any, vendor: any, reviews: any, wishlist?: any }) {
  const { data: session } = useSession()
  const pathname = usePathname() || ''
  const wishlistCtx = useWishlist()
  const { selectedVariant, selectedVariantId, setSelectedVariantId, isVariantAvailable, selectedOptions, selectOption, isSelectionPurchasable } = useVariantSelection(product)
  const variantImages = useMemo<string[]>(() => selectedVariant?.images || product?.images || [], [selectedVariant, product])
  const gallery = useProductGallery(variantImages)
  const { quantity, setQuantity, handleChange, handleBlur } = useProductQuantity({ variant: selectedVariant })
  const { addToCartWithRedirect, buyNow, buyNowLoading } = useCartActions(vendor)

  const [tab, setTab] = useState<Tab>('description')
  const [expandedDesc, setExpandedDesc] = useState(false)
  const [galleryTab, setGalleryTab] = useState<'images' | 'videos'>('images')
  const youtubeVideos = useMemo(() => getYoutubeVideos(product), [product])
  const [termsOpen, setTermsOpen] = useState(false)
  const [termsType, setTermsType] = useState<TermsType>('delivery')
  const [storepayOpen, setStorepayOpen] = useState(false)
  const [pocketOpen, setPocketOpen] = useState(false)
  const [lendOpen, setLendOpen] = useState(false)

  const openTerms = (t: TermsType) => {
    setTermsType(t)
    setTermsOpen(true)
  }

  const { loyaltySummary, loyaltyCategoryParents } = useCart()
  const discounts = getDisplayDiscounts(product, !!session?.user?.id, selectedVariant?.id)
  const effectivePrice = getEffectivePrice(selectedVariant, discounts)
  const strikethroughPrice = getStrikethroughPrice(selectedVariant, discounts)
  const showsDiscountBadge = discounts?.length > 0 && isDiscountBetter(selectedVariant, discounts)
  const loyaltyDeal = getLoyaltyProductDiscount({ product, variant: selectedVariant, summary: loyaltySummary, categoryParents: loyaltyCategoryParents, hasOtherDiscount: showsDiscountBadge })
  const displayEffective = loyaltyDeal ? Math.max(0, (effectivePrice ?? selectedVariant?.price ?? 0) - loyaltyDeal.unitSaving) : effectivePrice
  const displayStrike = loyaltyDeal ? (selectedVariant?.price ?? 0) : strikethroughPrice
  const isDiscounted = displayEffective !== null
  const variantSalePct = getSalePercent(selectedVariant)
  const badgePct = showsDiscountBadge
    ? (discounts.find((d: any) => d.type === 'productDiscount' && d.valueType === 'percent')?.valuePercent)
    : (variantSalePct > 0 && !loyaltyDeal?.saleOffset ? variantSalePct : null)
  const finalPrice = effectivePrice ?? selectedVariant?.price ?? 0
  const displayFinal = displayEffective ?? selectedVariant?.price ?? 0
  const stock = selectedVariant?.stock ?? 0
  const isOutOfStock = !isSelectionPurchasable || (!!product?.isTrackable && stock <= 0)
  const inWishlist = wishlistCtx.isInWishlist(product?.id, selectedVariant?.id)
  const showWishlist = isCustomerAuthActive(vendor)
  const rating = reviews?.average || product?.averageRating || 0
  const reviewsTotal = reviews?.totalCount || reviews?.total || product?.reviewCount || 0
  const canReview = !!session?.user?.id
  const variants = product?.variants || []
  const hasMultipleVariants = variants.length > 1 && !hasOptions(product)
  const dimensions = getVariantDimensions(selectedVariant)

  const onWishlistToggle = () => {
    if (selectedVariant?.id) wishlistCtx.toggle({ productId: product.id, variantId: selectedVariant.id })
  }

  const [addingToCart, setAddingToCart] = useState(false)
  const onAddToCart = async () => {
    const q = typeof quantity === 'number' ? quantity : 1
    setAddingToCart(true)
    try { await addToCartWithRedirect({ productId: product.id, variantId: selectedVariant?.id, quantity: q }) }
    finally { setAddingToCart(false) }
  }

  const onBuyNow = async () => {
    const q = typeof quantity === 'number' ? quantity : 1
    await buyNow({ productId: product.id, variantId: selectedVariant?.id, quantity: q })
  }

  const decQty = () => {
    const q = typeof quantity === 'number' ? quantity : 1
    setQuantity(Math.max(1, q - 1))
  }
  const incQty = () => {
    const q = typeof quantity === 'number' ? quantity : 1
    const maxQ = product?.isTrackable ? stock : Infinity
    setQuantity(Math.min(maxQ, q + 1))
  }

  const stripHtml = (s: string) => s?.replace(/<[^>]*>/g, '')?.replace(/&nbsp;/g, ' ')?.replace(/&amp;/g, '&')?.replace(/&lt;/g, '<')?.replace(/&gt;/g, '>')?.replace(/\s+/g, ' ').trim() || ''
  const descClean = stripHtml(product?.description || '')
  const descIsLong = descClean.length > 320

  return (
    <section className="bg-cream pt-6 pb-16">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
        <nav className="text-[13px] text-foreground/55 mb-3">
          <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
          <span className="mx-1.5">/</span>
          <a href={navigateUrl('/product', pathname)} className="hover:text-foreground">Бүтээгдэхүүн</a>
          {product?.category?.name && (
            <>
              <span className="mx-1.5">/</span>
              <a href={navigateUrl(`/product?category=${product.category.slug || product.category.id}`, pathname)} className="hover:text-foreground">{product.category.name}</a>
            </>
          )}
          <span className="mx-1.5">/</span>
          <span className="text-foreground truncate">{product?.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14">
          <div>
            {youtubeVideos.length > 0 && (
              <div className="mb-4 inline-flex items-center p-1 rounded-full bg-white border border-foreground/10">
                <button onClick={() => setGalleryTab('images')} className={`h-9 px-5 rounded-full text-[13px] font-semibold transition-colors cursor-pointer ${galleryTab === 'images' ? 'bg-foreground text-white' : 'text-foreground/60 hover:text-foreground'}`}>
                  Зураг
                </button>
                <button onClick={() => setGalleryTab('videos')} className={`h-9 px-5 rounded-full text-[13px] font-semibold transition-colors cursor-pointer inline-flex items-center gap-1.5 ${galleryTab === 'videos' ? 'bg-foreground text-white' : 'text-foreground/60 hover:text-foreground'}`}>
                  <span className="text-[9px]">▶</span> Видео
                </button>
              </div>
            )}
            {galleryTab === 'images' ? (
              <div className="relative aspect-square rounded-2xl bg-white overflow-hidden" onTouchStart={gallery.handleTouchStart} onTouchMove={gallery.handleTouchMove} onTouchEnd={gallery.handleTouchEnd}>
                {(badgePct || loyaltyDeal) ? (
                  <div className="absolute top-4 left-4 z-10 flex flex-wrap gap-1">
                    {badgePct ? (
                      <span className="inline-flex items-center px-2.5 h-7 rounded-full bg-[#FFC2DD] text-foreground text-[13px] font-semibold">
                        -{badgePct}%
                      </span>
                    ) : null}
                    {loyaltyDeal && (
                      <span className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-violet-600 text-white text-[13px] font-semibold tabular-nums">
                        <Medal className="size-3.5" />
                        -{loyaltyDeal.percent}%
                      </span>
                    )}
                  </div>
                ) : null}
                {showWishlist && (
                  <button
                    onClick={onWishlistToggle}
                    aria-label="Хадгалах"
                    className="absolute top-4 right-4 z-10 w-10 h-10 inline-flex items-center justify-center rounded-full bg-cream hover:bg-[#E9E4DA] text-foreground transition-colors cursor-pointer"
                  >
                    {wishlistCtx.loading ? <LoaderCircle className="size-4 animate-spin" /> : <Heart className={`size-4 ${inWishlist ? 'fill-[#B12A4E] text-[#B12A4E]' : ''}`} />}
                  </button>
                )}
                {gallery.activeImage && (
                  <img src={formatImageURL(gallery.activeImage)} alt={product?.name} className="w-full h-full object-cover" />
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {youtubeVideos.map((url: string, i: number) => {
                  const videoId = getYoutubeVideoId(url)
                  if (!videoId) return null
                  return (
                    <div key={i} className="relative w-full rounded-2xl overflow-hidden bg-black" style={{ paddingBottom: '56.25%' }}>
                      <iframe src={`https://www.youtube.com/embed/${videoId}`} title={`${product?.name} video ${i + 1}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute top-0 left-0 w-full h-full border-0" />
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div>
            <h1 className="text-[28px] sm:text-[36px] leading-[1.1] font-semibold font-gilroy tracking-[-0.012em] text-foreground">
              {product?.name}
            </h1>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {product?.brand?.name && (
                <a href={navigateUrl(`/product?brand=${product.brand.id}`, pathname)} className="text-[13px] font-semibold tracking-[0.14em] uppercase text-foreground hover:opacity-70">
                  {product.brand.name}
                </a>
              )}
              {product?.isLimitedEdition && (
                <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-[#FFE6B0] text-foreground text-[11px] font-semibold uppercase tracking-[0.08em]">
                  <Crown className="size-3" /> Limited
                </span>
              )}
              {product?.isDigital && (
                <button onClick={() => setTab('digital')} className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-[#DBEAFE] text-[#1d4ed8] text-[11px] font-semibold uppercase tracking-[0.08em] hover:opacity-80 transition-opacity cursor-pointer">
                  <FileDown className="size-3" /> Дижитал
                </button>
              )}
              {product?.isOrderBased && (
                <button onClick={() => setTab('order')} className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-[#DCFCE7] text-[#15803d] text-[11px] font-semibold uppercase tracking-[0.08em] hover:opacity-80 transition-opacity cursor-pointer">
                  <Hammer className="size-3" /> Захиалгаар
                </button>
              )}
            </div>
            {vendor?.canReview && rating > 0 && (
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <Stars value={rating} size={16} />
                <span className="text-[14px] font-semibold text-foreground">{rating.toFixed(1)}</span>
                {reviewsTotal > 0 && (
                  <>
                    <span className="text-foreground/30">·</span>
                    <button onClick={() => setTab('reviews')} className="text-[14px] text-foreground/60 hover:text-foreground hover:underline cursor-pointer">
                      {reviewsTotal} сэтгэгдэл
                    </button>
                  </>
                )}
              </div>
            )}

            {variantImages.length > 1 && (
              <div className="mt-6 grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-6 gap-2.5">
                {variantImages.map((src: string, i: number) => (
                  <button
                    key={i}
                    onClick={() => gallery.selectIndex(i)}
                    className={`aspect-square rounded-lg overflow-hidden bg-white border-2 transition-colors cursor-pointer ${i === gallery.activeImageIndex ? 'border-foreground' : 'border-transparent hover:border-foreground/30'}`}
                  >
                    <img src={formatImageURL(src)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {hasOptions(product) && (
              <div className="mt-6">
                <VariantOptionPicker product={product} selected={selectedOptions} onSelect={selectOption} selectedClassName="bg-foreground text-white border-foreground" />
              </div>
            )}

            {hasMultipleVariants && (() => {
              const showThumbs = variants.some((v: any) => v.images?.length > 0)
              return (
                <div className="mt-6">
                  <div className="flex items-baseline gap-2 text-sm font-medium text-foreground">
                    <span>Хувилбар{selectedVariant?.name && <span className="text-foreground/55">: {selectedVariant.name}</span>}</span>
                    {vendor?.showStock && product?.isTrackable && (
                      <span className={`text-[12px] tabular-nums ${stock > 0 ? 'text-foreground/55' : 'text-red-500 font-medium'}`}>
                        {stock > 0 ? `(${stock}ш үлд.)` : '(Дууссан)'}
                      </span>
                    )}
                  </div>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    {variants.map((v: any) => {
                      const isActive = v.id === selectedVariantId
                      const vImage = v.images?.[0] || product?.images?.[0]
                      const vDiscounts = getDisplayDiscounts(product, !!session?.user?.id, v.id)
                      const vEffective = getEffectivePrice(v, vDiscounts)
                      const vLoyaltyDeal = getLoyaltyProductDiscount({ product, variant: v, summary: loyaltySummary, categoryParents: loyaltyCategoryParents, hasOtherDiscount: vDiscounts?.length > 0 && isDiscountBetter(v, vDiscounts) })
                      const vBase = vEffective ?? (v.priceSale && v.priceSale > 0 && v.priceSale < v.price ? v.priceSale : v.price)
                      const vPrice = vLoyaltyDeal ? Math.max(0, (vEffective ?? v.price ?? 0) - vLoyaltyDeal.unitSaving) : vBase
                      const isAvailable = isVariantAvailable(v)
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => setSelectedVariantId(v.id)}
                          aria-pressed={isActive}
                          className={`inline-flex items-center gap-2.5 h-12 rounded-full border text-left transition-colors cursor-pointer ${showThumbs ? 'pl-1.5 pr-4' : 'px-4'} ${isActive ? 'bg-foreground text-white border-foreground' : 'bg-white text-foreground border-foreground/10 hover:border-foreground/30'} ${!isAvailable && !isActive ? 'opacity-60' : ''}`}
                        >
                          {showThumbs && (
                            <span className={`w-9 h-9 rounded-full overflow-hidden bg-cream shrink-0 ${!isAvailable ? 'grayscale' : ''}`}>
                              {vImage && <img src={formatImageURL(vImage)} alt={v.name} className="w-full h-full object-cover" />}
                            </span>
                          )}
                          <span className="flex flex-col leading-tight min-w-0">
                            <span className={`text-[13.5px] font-semibold truncate max-w-45 ${!isAvailable ? 'line-through' : ''}`}>{v.name}</span>
                            <span className={`text-[11.5px] tabular-nums ${isActive ? 'text-white/70' : 'text-foreground/55'}`}>{isAvailable ? (vPrice === 0 ? 'Үнэгүй' : formatPrice(vPrice)) : 'Дууссан'}</span>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })()}

            <div className="mt-7">
              <div className="flex items-end gap-3 flex-wrap">
                {isDiscounted ? (
                  <>
                    <span className="text-[40px] font-bold font-gilroy leading-none tracking-[-0.02em] text-foreground">{displayFinal === 0 ? 'Үнэгүй' : formatPrice(displayFinal)}</span>
                    <span className="pb-1 text-[16px] text-foreground/40 line-through">{formatPrice(displayStrike)}</span>
                    {badgePct ? (
                      <span className="mb-1 inline-flex items-center h-6 px-2 rounded-md bg-rose-100 text-rose-700 text-[12px] font-semibold">
                        −{badgePct}% хэмнэлт
                      </span>
                    ) : null}
                    {loyaltyDeal && (
                      <span className="mb-1 inline-flex items-center gap-1 h-6 px-2 rounded-md bg-violet-600 text-white text-[12px] font-semibold tabular-nums">
                        <Medal className="size-3.5" />
                        -{loyaltyDeal.percent}%
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-[40px] font-bold font-gilroy leading-none tracking-[-0.02em] text-foreground">{displayFinal === 0 ? 'Үнэгүй' : formatPrice(displayFinal)}</span>
                )}
              </div>
              {loyaltyDeal && (
                <div className="mt-3 inline-flex items-center gap-1.5 rounded-2xl bg-violet-50 border border-violet-200 text-violet-700 px-3.5 py-2 text-[12.5px] font-medium">
                  <Medal className="size-4 shrink-0" />
                  <span>{loyaltyDeal.tierName} түвшний хөнгөлөлт {loyaltyDeal.percent}%-д хамрагдах бараа тул <span className="font-semibold tabular-nums">{formatPrice(loyaltyDeal.finalPrice)}</span> хөнгөлөлт захиалгад автоматаар тооцогдоно</span>
                </div>
              )}
            </div>

            <DiscountLedger
              discounts={[
                ...(isDiscountBetter(selectedVariant, discounts) ? discounts.filter((d: any) => d.type === 'productDiscount') : []),
                ...discounts.filter((d: any) => d.type === 'buyXgetY'),
                ...discounts.filter((d: any) => d.type === 'freeShipping'),
              ]}
              pathname={pathname}
            />

            <div className="mt-6 flex items-center justify-between gap-4">
              <span className="text-sm font-medium text-foreground">Тоо ширхэг</span>
              <div className="inline-flex items-center h-11 rounded-full bg-white overflow-hidden">
                <button onClick={decQty} aria-label="Хасах" className="w-11 h-11 inline-flex items-center justify-center text-foreground hover:bg-foreground/5 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed" disabled={isOutOfStock || (typeof quantity === 'number' && quantity <= 1)}>
                  <Minus className="size-3.5" />
                </button>
                <input
                  type="text"
                  inputMode="numeric"
                  value={quantity}
                  onChange={(e) => handleChange(e.target.value)}
                  onBlur={handleBlur}
                  className="w-10 text-center bg-transparent border-0 outline-none focus:ring-0 text-[14px] font-semibold text-foreground"
                />
                <button onClick={incQty} aria-label="Нэмэх" className="w-11 h-11 inline-flex items-center justify-center text-foreground hover:bg-foreground/5 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed" disabled={isOutOfStock || (product?.isTrackable && typeof quantity === 'number' && quantity >= stock)}>
                  <Plus className="size-3.5" />
                </button>
              </div>
            </div>

            <div className="mt-5 space-y-2.5">
              <Button
                variant="default"
                size="lg"
                width="full"
                color={vendor?.color?.primary}
                className="rounded-full! h-12!"
                onClick={onAddToCart}
                isLoading={addingToCart}
                isActive={!isOutOfStock}
                icon={ShoppingBag}
              >
                {isOutOfStock ? 'Дууссан' : `Сагсанд нэмэх — ${formatPrice(displayFinal * (typeof quantity === 'number' ? quantity : 1))}`}
              </Button>
              <button
                onClick={onBuyNow}
                disabled={isOutOfStock || buyNowLoading}
                className="w-full h-12 rounded-full bg-foreground text-white font-semibold text-[15px] inline-flex items-center justify-center gap-2 hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {buyNowLoading ? <LoaderCircle className="size-4 animate-spin" /> : 'Шууд авах'}
              </button>
            </div>

            <DetailsList
              tags={getProductTags(product) || []}
              pathname={pathname}
            />

            <div className="mt-5 pt-3 border-t border-foreground/10 grid grid-cols-3">
              {([
                { id: 'delivery' as TermsType, label: 'Хүргэлт', Icon: Truck },
                { id: 'service' as TermsType, label: 'Үйлчилгээ', Icon: Handshake },
                { id: 'payment' as TermsType, label: 'Төлбөр', Icon: BanknoteArrowUp },
              ]).map((t, i) => (
                <button
                  key={t.id}
                  onClick={() => openTerms(t.id)}
                  className={`inline-flex items-center justify-center gap-2 h-8 px-2 text-[13.5px] font-semibold text-foreground/70 hover:text-foreground transition-colors cursor-pointer ${i > 0 ? 'border-l border-foreground/10' : ''}`}
                >
                  <t.Icon className="size-4" />
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <TermsDrawer isOpen={termsOpen} onClose={() => setTermsOpen(false)} type={termsType} terms={vendor?.terms || {}} vendor={vendor} />
        <StorepayDrawer isOpen={storepayOpen} onClose={() => setStorepayOpen(false)} vendor={vendor} productPrice={finalPrice} productName={product?.name} productImage={gallery.activeImage ? formatImageURL(gallery.activeImage) : ''} />
        <PocketDrawer isOpen={pocketOpen} onClose={() => setPocketOpen(false)} vendor={vendor} productPrice={finalPrice} productName={product?.name} productImage={gallery.activeImage ? formatImageURL(gallery.activeImage) : ''} />
        <LendDrawer isOpen={lendOpen} onClose={() => setLendOpen(false)} vendor={vendor} productPrice={finalPrice} productName={product?.name} productImage={gallery.activeImage ? formatImageURL(gallery.activeImage) : ''} />

        <div className="mt-12 border-b border-foreground/10">
          <div className="flex flex-wrap items-center gap-2">
            {(() => {
              const isAllowed = (key: string) => product?.paymentOptions?.[key] !== false
              const showQpay = vendor?.qpayMerchant && vendor?.canQpay !== false && isAllowed('qpay')
              const showTransfer = vendor?.canTransfer && vendor?.canSimple !== false && isAllowed('transfer')
              const showStorepay = vendor?.storepayCredentials && vendor?.canStorepay !== false && isAllowed('storepay')
              const showPocket = vendor?.pocketCredentials && vendor?.canPocket !== false && isAllowed('pocket')
              const showLend = vendor?.lendCredentials && vendor?.canLend !== false && isAllowed('lend')
              const showOmniway = vendor?.omniwayCredentials && vendor?.canOmniway !== false && isAllowed('omniway')
              const hasPayment = showQpay || showTransfer || showStorepay || showPocket || showLend || showOmniway
              return ([
                { id: 'description' as Tab, label: 'Дэлгэрэнгүй' },
                ...(dimensions ? [{ id: 'dimensions' as Tab, label: 'Хэмжээс' }] : []),
                ...(product?.isDigital ? [{ id: 'digital' as Tab, label: 'Дижитал' }] : []),
                ...(product?.isOrderBased ? [{ id: 'order' as Tab, label: 'Захиалга' }] : []),
                ...(hasPayment ? [{ id: 'payment' as Tab, label: 'Төлбөр' }] : []),
                { id: 'reviews' as Tab, label: 'Сэтгэгдэл' },
              ] as { id: Tab, label: string }[])
            })().map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`h-12 px-1 mr-6 -mb-px border-b-2 text-[15px] transition-colors shrink-0 cursor-pointer ${tab === t.id ? 'border-foreground text-foreground font-semibold' : 'border-transparent text-foreground/55 hover:text-foreground'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          {tab === 'description' && (
            <div className="text-[15px] leading-7 text-foreground/85">
              {product?.description ? (
                <>
                  <div className={`relative ${descIsLong && !expandedDesc ? 'max-h-72 overflow-hidden' : ''}`}>
                    <ProductDescription html={product.description} productName={product.name} vendor={vendor} className="product-description" />
                    {descIsLong && !expandedDesc && (
                      <div className="absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-cream to-transparent pointer-events-none" />
                    )}
                  </div>
                  {descIsLong && (
                    <button onClick={() => setExpandedDesc(s => !s)} className="mt-4 inline-flex items-center gap-1 text-[14px] font-semibold text-foreground hover:opacity-70 cursor-pointer">
                      {expandedDesc ? 'Хураах' : 'Дэлгэрэнгүй'}
                      <ChevronDown className={`size-4 transition-transform ${expandedDesc ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </>
              ) : (
                <p className="text-foreground/55">Тайлбар оруулаагүй байна.</p>
              )}
            </div>
          )}

          {tab === 'dimensions' && dimensions && (
            <div className="text-[15px] text-foreground/85">{dimensions}</div>
          )}

          {tab === 'digital' && (
            <div className="flex items-start gap-4">
              <span className="h-11 w-11 rounded-xl grid place-items-center shrink-0 bg-[#DBEAFE]">
                <FileDown className="h-5 w-5 text-[#1d4ed8]" strokeWidth={2} />
              </span>
              <div className="flex-1 min-w-0">
                <h3 className="text-[16px] font-semibold text-foreground leading-tight">Дижитал бүтээгдэхүүн</h3>
                <p className="mt-1.5 text-[14px] text-foreground/65 leading-relaxed">Худалдан авалт амжилттай болсны дараа татах файлд хандах эрх нээгдэнэ. Файлыг захиалгын хуудаснаас татах боломжтой.</p>
                {selectedVariant?.digitalFile && (
                  <div className="mt-4 max-w-sm"><FileRow file={selectedVariant.digitalFile} /></div>
                )}
              </div>
            </div>
          )}

          {tab === 'order' && (
            <div className="flex items-start gap-4">
              <span className="h-11 w-11 rounded-xl grid place-items-center shrink-0 bg-[#DCFCE7]">
                <Hammer className="h-5 w-5 text-[#15803d]" strokeWidth={2} />
              </span>
              <div className="flex-1 min-w-0">
                <h3 className="text-[16px] font-semibold text-foreground leading-tight">Захиалгаар бэлтгэдэг</h3>
                <p className="mt-1.5 text-[14px] text-foreground/65 leading-relaxed">Энэхүү бүтээгдэхүүнийг таны захиалгын дараа тусгайлан бэлтгэнэ. Бэлэн болсны дараа хүргэлт эхэлнэ.</p>
                {product?.orderBasedPeriod && (
                  <div className="mt-4 inline-flex items-center gap-2 h-9 px-3.5 rounded-full bg-foreground/5 text-foreground text-[13px] font-semibold tabular-nums">
                    Бэлтгэх хугацаа: {product.orderBasedPeriod}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'reviews' && (
            <Review product={product} vendor={vendor} reviews={reviews} canReview={canReview} />
          )}

          {tab === 'features' && (
            <div className="max-w-3xl">
              {product?.features?.length > 0 ? (
                <ul className="divide-y divide-foreground/10">
                  {product.features.map((f: any, i: number) => (
                    <li key={i} className="grid grid-cols-2 sm:grid-cols-3 gap-4 py-3 text-[14px]">
                      <span className="text-foreground/55 col-span-1">{f.name || f.label}</span>
                      <span className="text-foreground sm:col-span-2">{f.value}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-foreground/55 text-[14px]">Онцлог оруулаагүй байна.</p>
              )}
            </div>
          )}

          {tab === 'payment' && (() => {
            const isAllowed = (key: string) => product?.paymentOptions?.[key] !== false
            const showQpay = vendor?.qpayMerchant && vendor?.canQpay !== false && isAllowed('qpay')
            const showTransfer = vendor?.canTransfer && vendor?.canSimple !== false && isAllowed('transfer')
            const showStorepay = vendor?.storepayCredentials && vendor?.canStorepay !== false && isAllowed('storepay')
            const showPocket = vendor?.pocketCredentials && vendor?.canPocket !== false && isAllowed('pocket')
            const showLend = vendor?.lendCredentials && vendor?.canLend !== false && isAllowed('lend')
            const showOmniway = vendor?.omniwayCredentials && vendor?.canOmniway !== false && isAllowed('omniway')
            return (
              <div className="max-w-full">
                <p className="text-[14px] text-foreground/60 mb-1.5">Та энэхүү бүтээгдэхүүнийг дараах төлбөрийн хэлбэрээр худалдан авах боломжтой.</p>
                <div className="divide-y divide-foreground/10 border-foreground/10">
                  {showQpay && (
                    <PaymentRow logo={Qpay} title="QPay" subtitle="Бүх банкны аппаар QR код уншуулан төлөх" />
                  )}
                  {showTransfer && (
                    <PaymentRow logo={TransferIcon} title="Дансаар шилжүүлэх" subtitle="Дэлгүүрийн дансанд шилжүүлэг хийж төлөх" />
                  )}
                  {showStorepay && (
                    <PaymentRow logo={Storepay} title="StorePay" subtitle="Үнийн дүнг 4-6 хуваан төлөх боломжтой" minAmount="100,000₮+" onClick={() => setStorepayOpen(true)} logoRounded />
                  )}
                  {showPocket && (
                    <PaymentRow logo={Pocket} title="PocketZero" subtitle="Урьдчилгаагүй, шимтгэлгүй 30-90 хоногт 2-6 хувааж төлөх" minAmount="100,000₮+" onClick={() => setPocketOpen(true)} logoRounded />
                  )}
                  {showLend && (
                    <PaymentRow logo={Lend} title="LendMN" subtitle="Урьдчилгаагүй, шимтгэлгүй 30-60 хоногт 1-4 хувааж төлөх" minAmount="100,000₮+" onClick={() => setLendOpen(true)} />
                  )}
                  {showOmniway && (
                    <PaymentRow logo={Omniway} title="OmniWay" subtitle="Хуваан төлөх боломжтой" minAmount="100,000₮+" logoRounded />
                  )}
                </div>
              </div>
            )
          })()}
        </div>

        {productsList && productsList.length > 0 && (
          <div className="mt-16">
            <RelatedRail
              title={product?.brand?.name ? `${product.brand.name} брэндийн ижил төстэй бараа` : 'Танд таалагдаж магадгүй'}
              products={productsList.filter((p: any) => p.id !== product?.id).slice(0, 12)}
              vendor={vendor}
            />
          </div>
        )}
      </div>
    </section>
  )
}

function FileRow({ file }: { file: { name: string, size: number } }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl bg-cream border border-foreground/10 px-3 py-2 text-[12.5px]">
      <FileText className="h-4 w-4 shrink-0 text-foreground/55" strokeWidth={1.75} />
      <span className="font-semibold truncate flex-1 text-foreground">{file.name}</span>
      <span className="tabular-nums text-foreground/55 shrink-0">{formatFileSize(file.size)}</span>
      <Download className="h-4 w-4 shrink-0 text-foreground/55" strokeWidth={1.75} />
    </div>
  )
}

function PaymentRow({ logo, title, subtitle, minAmount, onClick, logoRounded }: { logo: any, title: string, subtitle: string, minAmount?: string, onClick?: () => void, logoRounded?: boolean }) {
  const isInteractive = !!onClick
  const Tag: any = isInteractive ? 'button' : 'div'
  return (
    <Tag
      onClick={onClick}
      className={`group flex items-center gap-4 w-full py-4 text-left ${isInteractive ? 'cursor-pointer' : ''}`}
    >
      <Image src={logo} alt={title} className={`h-10 w-10 shrink-0 ${logoRounded ? 'rounded-md object-cover' : 'object-contain'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[15px] font-semibold text-foreground leading-tight">{title}</span>
          {minAmount && (
            <span className="text-[11.5px] text-foreground/45 tabular-nums tracking-tight">· {minAmount}-аас</span>
          )}
        </div>
        <p className="mt-1 text-[13px] text-foreground/55 leading-snug">{subtitle}</p>
      </div>
      {isInteractive && (
        <ChevronRight className="h-4 w-4 text-foreground/30 group-hover:text-foreground/70 group-hover:translate-x-0.5 transition-all shrink-0" />
      )}
    </Tag>
  )
}

const LEDGER_TONE: Record<string, { dot: string, label: string, badgeBg: string, badgeFg: string }> = {
  productDiscount: { dot: '#2563eb', label: 'Хямдрал', badgeBg: '#dbeafe', badgeFg: '#1d4ed8' },
  buyXgetY: { dot: '#16a34a', label: 'Бэлэг', badgeBg: '#dcfce7', badgeFg: '#15803d' },
  freeShipping: { dot: '#0d9488', label: 'Хүргэлт', badgeBg: '#ccfbf1', badgeFg: '#0f766e' },
}

function DiscountLedger({ discounts, pathname }: { discounts: any[], pathname: string }) {
  const [open, setOpen] = useState(true)
  if (!discounts || discounts.length === 0) return null
  const maxPct = discounts.filter(d => d.type === 'productDiscount' && d.valueType === 'percent').reduce((m, d) => Math.max(m, d.valuePercent || 0), 0)
  const hasBx = discounts.some(d => d.type === 'buyXgetY')
  const hasFs = discounts.some(d => d.type === 'freeShipping')
  const summary = [maxPct > 0 && `-${maxPct}% хүртэл`, hasBx && 'Бэлэгтэй', hasFs && 'Үнэгүй хүргэлт'].filter(Boolean).join(' · ')
  return (
    <div className="mt-5 rounded-2xl bg-white border border-foreground/10 overflow-hidden">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-cream/60 transition-colors cursor-pointer">
        <div className="h-8 w-8 grid place-items-center rounded-lg shrink-0" style={{ background: 'linear-gradient(135deg,#dbeafe,#dcfce7)' }}>
          <Percent className="h-4 w-4 text-blue-700" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold leading-tight text-foreground">{discounts.length} урамшуулал идэвхтэй</div>
          {summary && <div className="mt-0.5 text-[12px] text-foreground/55 leading-snug truncate">{summary}</div>}
        </div>
        <ChevronDown className={`h-4 w-4 text-foreground/55 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="border-t border-foreground/10">
          {discounts.map((d, i) => (
            <div key={d.id || i} className={i < discounts.length - 1 ? 'border-b border-foreground/10' : ''}>
              <DiscountLedgerRow d={d} pathname={pathname} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function DiscountLedgerRow({ d, pathname }: { d: any, pathname: string }) {
  const tone = LEDGER_TONE[d.type as keyof typeof LEDGER_TONE]
  if (!tone) return null
  if (d.type === 'productDiscount') {
    const badge = d.valueType === 'percent' && d.valuePercent ? `-${d.valuePercent}%` : d.valueType === 'amount' && d.valueAmount ? `-${formatPrice(d.valueAmount)}` : null
    return (
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: tone.dot }} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-foreground/55">{tone.label}</span>
            {badge && <span className="text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded" style={{ background: tone.badgeBg, color: tone.badgeFg }}>{badge}</span>}
          </div>
          <div className="mt-0.5 text-[13px] font-semibold leading-tight text-foreground">{d.name}</div>
          <div className="mt-0.5 text-[12.5px] text-foreground/55 leading-snug">Энэхүү урамшуулал бараан дээр идэвхтэй байна.</div>
          {d.haveCampaignPage && d.campaignPageHandle && (
            <a href={navigateUrl(`/c/${d.campaignPageHandle}`, pathname)} className="inline-flex items-center gap-1 mt-1.5 text-[12px] font-medium text-blue-600 hover:underline">
              Урамшууллын хуудас үзэх <ArrowRight className="h-3 w-3" strokeWidth={2.25} />
            </a>
          )}
        </div>
      </div>
    )
  }
  if (d.type === 'buyXgetY') {
    const buyX = d.buyXMinQuantity || 1
    const getY = d.getYMaxQuantity || 1
    const reward = d.rewardProducts?.[0] || {}
    const free = reward.valueType === 'free' || !reward.valueType
    const pctVal = reward.valuePercent || d.valuePercent || 0
    const triggers = d.triggerProducts || []
    const isAll = d.buyXMatchMode === 'all' && triggers.length > 1
    const fallbackTitle = getBuyXGetYFallbackTitle(d)
    const tagText = getBuyXGetYTagText(d)
    return (
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: tone.dot }} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-foreground/55">{tone.label}</span>
            <span className="text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded" style={{ background: tone.badgeBg, color: tone.badgeFg }}>{tagText}</span>
          </div>
          <div className="mt-0.5 text-[13px] font-semibold leading-tight text-foreground">{d.name || fallbackTitle}</div>
          <div className="mt-0.5 text-[12.5px] text-foreground/55 leading-snug">
            {isAll ? `Тус бүрээс багадаа ${buyX} ширхэг авбал ` : `${buyX} авбал `}
            {free ? `${getY} бэлэг үнэгүй.` : `${getY} бэлэг ${pctVal}% хямдралтай.`}
          </div>
          {isAll && triggers.length > 0 && (
            <div className="mt-2.5 grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(triggers.length, 3)}, 1fr)` }}>
              {triggers.map((tp: any) => {
                const hasDiscount = tp.priceSale && tp.priceSale > 0 && tp.priceSale < tp.price
                const eff = hasDiscount ? tp.priceSale : tp.price
                return (
                  <a key={tp.id} href={navigateUrl(`/product/${tp.customId || tp.id}`, pathname)} className="flex items-center gap-2 p-2 rounded-xl border border-foreground/10 hover:border-foreground/30 transition-colors">
                    {tp.image && <img src={formatImageURL(tp.image)} alt="" className="h-9 w-9 rounded-md object-cover bg-cream shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-[11.5px] font-semibold leading-tight truncate text-foreground">{tp.name}</div>
                      {tp.price != null && (
                        <div className="mt-0.5 flex items-baseline gap-1.5">
                          <span className={`text-[11px] tabular-nums ${hasDiscount ? 'text-red-600 font-semibold' : 'text-foreground'}`}>{formatPrice(eff)}</span>
                          {hasDiscount && <span className="text-[10px] tabular-nums line-through text-foreground/45">{formatPrice(tp.price)}</span>}
                        </div>
                      )}
                    </div>
                  </a>
                )
              })}
            </div>
          )}
        </div>
      </div>
    )
  }
  if (d.type === 'freeShipping') {
    return (
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: tone.dot }} />
        <div className="min-w-0 flex-1">
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-foreground/55">{tone.label}</span>
          <div className="mt-0.5 text-[13px] font-semibold leading-tight text-foreground">Үнэгүй хүргэлт</div>
          <div className="mt-0.5 text-[12.5px] text-foreground/55 leading-snug">
            <span className="font-semibold text-foreground">{d.name}</span> — энэхүү бараан дээр үнэгүй хүргэлт идэвхтэй.
          </div>
          {d.isCampaign && d.campaignName && (
            <div className="mt-1 text-[11.5px] text-foreground/55">{d.campaignName}</div>
          )}
        </div>
      </div>
    )
  }
  return null
}

function DetailsList({ tags, pathname }: { tags: string[], pathname: string }) {
  if (tags.length === 0) return null
  return (
    <div className="mt-5 rounded-2xl bg-white border border-foreground/10 divide-y divide-foreground/10">
      {tags.length > 0 && (
        <div className="px-4 py-3 flex items-start gap-4">
          <span className="text-[12px] uppercase tracking-[0.12em] font-semibold text-foreground/45 w-24 shrink-0 pt-1">Шошго</span>
          <div className="flex flex-wrap gap-1.5 flex-1">
            {tags.map((tag, i) => (
              <a key={i} href={navigateUrl(`/product?tag=${encodeURIComponent(tag)}`, pathname)} className="inline-flex items-center h-7 px-2.5 rounded-full bg-cream text-foreground text-[12.5px] font-semibold hover:bg-[#E9E4DA] transition-colors">
                {tag}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
